import React, { useState } from "react";
import {
  Bot,
  Check,
  ChevronDown,
  ChevronUp,
  Clock,
  Copy,
  Key,
  Sparkles,
} from "lucide-react";
import { apiClient } from "../../api/client";
import { AlertBanner, Button } from "../../components/common";
import { useLanguage } from "../../contexts/LanguageContext";
import { useGeminiTranslation } from "./geminiTranslations";

export interface GeminiConnectCardProps {
  onTokenCreated?: () => void;
  mcpServerUrl?: string;
}

const DEFAULT_MCP_URL = "https://chpl.priceloss.com/mcp/";

export const GeminiConnectCard: React.FC<GeminiConnectCardProps> = ({
  onTokenCreated,
  mcpServerUrl = DEFAULT_MCP_URL,
}) => {
  const { direction } = useLanguage();
  const t = useGeminiTranslation();

  const [token, setToken] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<
    "url" | "token" | "prompt" | null
  >(null);
  const [isGuideOpen, setIsGuideOpen] = useState(true);

  const copyToClipboard = async (
    text: string,
    field: "url" | "token" | "prompt",
  ) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2500);
    } catch {
      // Fallback if clipboard API is unavailable
    }
  };

  const handleGenerateToken = async () => {
    setIsGenerating(true);
    setError(null);
    try {
      const { data, error: apiErr } = await apiClient.POST(
        "/api/v1/plugin/tokens/gemini",
      );
      if (apiErr || !data) {
        setError(t("gemini.error"));
      } else {
        setToken(data.token);
        onTokenCreated?.();
      }
    } catch {
      setError(t("gemini.error"));
    } finally {
      setIsGenerating(false);
    }
  };

  const scheduledPromptText = t("gemini.scheduled_prompt");

  return (
    <div
      data-testid="gemini-connect-card"
      dir={direction}
      className="rounded-2xl border border-indigo-100 bg-linear-to-br from-indigo-50/40 via-white to-purple-50/20 p-6 shadow-xs dark:border-indigo-900/50 dark:from-slate-900 dark:via-slate-900 dark:to-indigo-950/20 space-y-5"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-linear-to-tr from-indigo-600 to-purple-600 text-white shadow-xs">
            <Sparkles className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-base font-bold text-slate-900 dark:text-slate-100">
              {t("gemini.title")}
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {t("gemini.description")}
            </p>
          </div>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700 dark:border-indigo-800 dark:bg-indigo-950/50 dark:text-indigo-300">
          <Bot className="h-3.5 w-3.5" />
          {t("gemini.badge")}
        </span>
      </div>

      {error && (
        <AlertBanner
          variant="error"
          message={error}
          onClose={() => setError(null)}
        />
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
            {t("gemini.server_url")}
          </label>
          <div className="flex items-center gap-2">
            <input
              type="text"
              readOnly
              value={mcpServerUrl}
              className="w-full rounded-lg border border-slate-200 bg-slate-50/80 px-3 py-2 font-mono text-xs text-slate-800 select-all dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
            />
            <Button
              size="sm"
              variant="outline"
              className="shrink-0 text-xs min-h-9"
              onClick={() => void copyToClipboard(mcpServerUrl, "url")}
              aria-label={t("gemini.copy")}
            >
              {copiedField === "url" ? (
                <Check className="h-4 w-4 text-emerald-600" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
              <span>
                {copiedField === "url" ? t("gemini.copied") : t("gemini.copy")}
              </span>
            </Button>
          </div>
          <p className="text-[11px] text-slate-500 dark:text-slate-400">
            {t("gemini.server_url_hint")}
          </p>
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
            {t("gemini.token_label")}
          </label>
          {token ? (
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={token}
                  className="w-full rounded-lg border border-emerald-300 bg-emerald-50/60 px-3 py-2 font-mono text-xs text-emerald-900 select-all dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200"
                />
                <Button
                  size="sm"
                  variant="primary"
                  className="shrink-0 text-xs min-h-9"
                  onClick={() => void copyToClipboard(token, "token")}
                  aria-label={t("gemini.copy")}
                >
                  {copiedField === "token" ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                  <span>
                    {copiedField === "token"
                      ? t("gemini.copied")
                      : t("gemini.copy")}
                  </span>
                </Button>
              </div>
              <p className="text-[11px] font-medium text-amber-700 dark:text-amber-400">
                {t("gemini.token_warning")}
              </p>
            </div>
          ) : (
            <div>
              <Button
                size="sm"
                variant="outline"
                className="w-full min-h-9 text-xs border-indigo-200 text-indigo-700 hover:bg-indigo-50 dark:border-indigo-800 dark:text-indigo-300 dark:hover:bg-indigo-950"
                isLoading={isGenerating}
                onClick={() => void handleGenerateToken()}
              >
                <Key className="h-3.5 w-3.5" />
                <span>
                  {isGenerating
                    ? t("gemini.generating_token")
                    : t("gemini.generate_token")}
                </span>
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200/80 bg-white/80 p-4 dark:border-slate-800 dark:bg-slate-800/40">
        <button
          type="button"
          onClick={() => setIsGuideOpen(!isGuideOpen)}
          className="flex w-full items-center justify-between text-start text-xs font-bold text-slate-800 dark:text-slate-200 cursor-pointer"
        >
          <span>{t("gemini.guide_title")}</span>
          {isGuideOpen ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </button>
        {isGuideOpen && (
          <div className="mt-3 space-y-2 text-xs leading-relaxed text-slate-600 dark:text-slate-300">
            <p>
              <bdi>{t("gemini.step_1")}</bdi>
            </p>
            <p>
              <bdi>{t("gemini.step_2")}</bdi>
            </p>
            <p>
              <bdi>{t("gemini.step_3")}</bdi>
            </p>
            <p>
              <bdi>{t("gemini.step_4")}</bdi>
            </p>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-purple-100 bg-purple-50/50 p-4 dark:border-purple-900/40 dark:bg-purple-950/20 space-y-2">
        <div className="flex items-center gap-2 text-xs font-bold text-purple-900 dark:text-purple-200">
          <Clock className="h-4 w-4 text-purple-600 dark:text-purple-400 shrink-0" />
          <span>{t("gemini.scheduled_title")}</span>
        </div>
        <p className="text-xs text-purple-800/80 dark:text-purple-300/80">
          {t("gemini.scheduled_desc")}
        </p>
        <div className="flex items-start gap-2 rounded-lg border border-purple-200 bg-white p-2.5 text-xs text-slate-700 select-all dark:border-purple-900/60 dark:bg-slate-900 dark:text-slate-200">
          <p className="flex-1 font-mono text-[11px] leading-relaxed">
            <bdi>{scheduledPromptText}</bdi>
          </p>
          <Button
            size="sm"
            variant="ghost"
            className="shrink-0 p-1 text-purple-700 hover:bg-purple-100 dark:text-purple-300 dark:hover:bg-purple-900"
            onClick={() => void copyToClipboard(scheduledPromptText, "prompt")}
            aria-label={t("gemini.copy")}
          >
            {copiedField === "prompt" ? (
              <Check className="h-3.5 w-3.5 text-emerald-600" />
            ) : (
              <Copy className="h-3.5 w-3.5" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
